import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Library, Download, ArrowLeft, Upload, Loader2, CheckSquare, Trash2, X } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useUploader, useActiveUploads } from "@/lib/useUploader";
import { api, errMsg, mediaUrl, saveAs } from "@/lib/api";
import TopNav from "@/components/TopNav";
import ImageViewer from "@/components/ImageViewer";
import NavArrows, { useListNav } from "@/components/NavArrows";
import UploadQueue from "@/components/UploadQueue";
import { KINDS, SOURCES, LibTile, filterLib, useLibrary, ProjectFilter } from "@/components/LibraryPicker";

export default function MediaLibrary() {
  const nav = useNavigate();
  const [tick, setTick] = useState(0);
  const items = useLibrary(true, tick);
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState((sessionStorage.getItem("ax_last_studio") || "").split("/")[2] || "");
  const [del, setDel] = useState(null);
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const upBusy = useActiveUploads();
  const fileRef = useRef(null);
  const [project, setProject] = useState(() => (sessionStorage.getItem("ax_last_studio") || "").split("/")[2] || "");
  const target = project || pid;
  const { upload } = useUploader(target, undefined, false);
  const [ups, setUps] = useState([]);
  const ctls = useRef({});
  const setUp = (id, p) => setUps((l) => l.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const pauseUp = (u) => { const c = ctls.current[u.id]; if (!c) return; c.paused = !c.paused; if (c.paused) c.ac?.abort(); setUp(u.id, { paused: c.paused }); };
  const cancelUp = (u) => { const c = ctls.current[u.id]; if (!c) return; c.cancelled = true; c.ac?.abort(); setUps((l) => l.filter((x) => x.id !== u.id)); toast.info(`${u.name} upload cancelled`); };
  useEffect(() => { api.get("/projects").then((r) => { setProjects(r.data); setPid((p) => (r.data.some((x) => x.id === p) ? p : r.data[0]?.id || "")); }).catch(() => {}); }, []);
  useEffect(() => { setTick((t) => t + 1); }, [upBusy]);
  const uploadFiles = async (files) => {
    if (!target) { toast.error("Create a project first"); return; }
    const list = Array.from(files || []);
    await Promise.all(list.map(async (f) => {
      const id = Math.random().toString(36).slice(2);
      const t = (f.type || (/\.mov$/i.test(f.name) ? "video/" : "")).split("/")[0];
      ctls.current[id] = { paused: false, cancelled: false };
      setUps((l) => [...l, { id, name: f.name, kind: t, thumb: URL.createObjectURL(f), pct: 0, phase: "uploading", paused: false }]);
      const r = await upload(f, (p) => setUp(id, { pct: p.pct, eta: p.eta, phase: p.phase }), ctls.current[id]);
      delete ctls.current[id];
      setUps((l) => l.filter((x) => x.id !== id));
      if (r) { setTick((n) => n + 1); if (!document.querySelector("[data-testid=media-library-page]")) toast.success(`${f.name} uploaded to Media Library`); }
    }));
  };
  const doDelete = async () => {
    try {
      if (del.bulk) {
        const { data } = await api.post("/library/delete", { ids: del.bulk });
        toast.success(`Deleted ${data.deleted} file${data.deleted === 1 ? "" : "s"} permanently`);
        setSel([]);
        setSelMode(false);
      } else {
        await api.delete(`/library/${encodeURIComponent(del.id)}`);
        toast.success("Deleted permanently");
      }
      setTick((t) => t + 1);
    } catch (e) { toast.error(errMsg(e, "Could not delete")); } finally { setDel(null); }
  };
  const [kind, setKind] = useState("");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [unused, setUnused] = useState("");
  const [unusedIds, setUnusedIds] = useState(null);
  useEffect(() => { if (unused) api.get("/library/unused").then((r) => setUnusedIds(new Set(r.data.ids))).catch(() => toast.error("Could not check unused media")); }, [unused, tick]);
  const shown = useMemo(() => {
    const base = filterLib(items, { kind, source, q, project });
    if (!unused || !unusedIds) return base;
    const cutoff = Date.now() - 30 * 86400000;
    return base.filter((x) => x.source === "upload" && unusedIds.has(x.id) && (unused === "unused" || new Date(x.created_at).getTime() < cutoff));
  }, [items, kind, source, q, project, unused, unusedIds]);
  const isImg = (open?.mime || "").startsWith("image/");
  const libNav = useListNav(shown, open, setOpen);
  useEffect(() => {
    if (!open || (open.mime || "").startsWith("image/")) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  const shownIds = useMemo(() => new Set(shown.map((x) => x.id)), [shown]);
  useEffect(() => { setSel((s) => s.filter((id) => shownIds.has(id))); }, [shownIds]);
  const allOn = shown.length > 0 && shown.every((x) => sel.includes(x.id));

  return (
    <div className="min-h-screen relative z-[1] text-zinc-100">
      <TopNav />
      <main className="max-w-[1600px] mx-auto px-6 py-8" data-testid="media-library-page"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}>
        <button type="button" onClick={() => (window.history.length > 1 ? nav(-1) : nav(sessionStorage.getItem("ax_last_studio") || "/projects"))}
          className="ax-chip mb-5" data-testid="media-back-button"><ArrowLeft className="w-3.5 h-3.5" />Back</button>
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold flex items-center gap-3"><Library className="w-9 h-9 text-[#7AF25F]" />Media Library</h1>
            <p className="text-sm text-zinc-400 mt-2">Every file you uploaded and every image or video you generated, across all projects. Upload new files here (or drop them on the page) and use them later from any upload box.</p>
          </div>
          <div className="ml-auto flex items-center gap-2" data-testid="media-upload">
            <input ref={fileRef} type="file" multiple accept="image/*,video/*,audio/*,.mov" className="hidden" data-testid="media-upload-input" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
            <button type="button" onClick={() => fileRef.current?.click()} className="h-10 px-4 rounded-lg ax-generate text-sm font-semibold flex items-center gap-2" data-testid="media-upload-button">
              {upBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{upBusy ? `Uploading ${upBusy}…` : "Upload new media"}</button>
          </div>
          <span className="w-full text-right text-xs font-mono text-zinc-500" data-testid="media-library-count">{items ? `${shown.length} of ${items.length} files` : "Loading…"}</span>
        </div>
        <UploadQueue items={ups} onPause={pauseUp} onCancel={cancelUp} />
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {KINDS.map(([k, l]) => <button key={k} type="button" onClick={() => setKind(k)} data-testid={`media-kind-${k || "all"}`} className={`ax-chip ${kind === k ? "!border-[#7AF25F]/60 text-white" : ""}`}>{l}</button>)}
          <span className="w-px h-5 bg-zinc-800 mx-1" />
          {SOURCES.map(([k, l]) => <button key={k} type="button" onClick={() => setSource(k)} data-testid={`media-source-${k || "all"}`} className={`ax-chip ${source === k ? "!border-[#7AF25F]/60 text-white" : ""}`}>{l}</button>)}
          <ProjectFilter value={project} onChange={setProject} testid="media-project-filter" />
          {[["unused", "Unused"], ["old", "Unused 30+ days"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setUnused((u) => (u === k ? "" : k))} data-testid={`media-unused-${k}`}
              className={`ax-chip ${unused === k ? "!border-amber-400/70 text-amber-200" : ""}`}>{l}</button>))}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, prompt or project" className="ax-input !h-9 w-72 text-xs ml-auto" data-testid="media-search" />
          {!selMode ? (
            <button type="button" onClick={() => setSelMode(true)} className="ax-chip" data-testid="media-select-mode"><CheckSquare className="w-3.5 h-3.5" />Select</button>
          ) : (
            <>
              <button type="button" onClick={() => setSel(allOn ? [] : shown.map((x) => x.id))} className="ax-chip" data-testid="media-select-all">
                {allOn ? "Clear selection" : `Select all (${shown.length})`}</button>
              <button type="button" disabled={!sel.length} onClick={() => setDel({ bulk: sel.filter((id) => shownIds.has(id)) })} data-testid="media-delete-selected"
                className="ax-chip !border-red-500/50 !text-red-300 hover:!bg-red-500/10 disabled:opacity-40"><Trash2 className="w-3.5 h-3.5" />Delete {sel.length || ""} selected</button>
              <button type="button" onClick={() => { setSelMode(false); setSel([]); }} className="ax-icon-chip" data-testid="media-select-cancel" title="Done"><X className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8 gap-3" data-testid="media-library-grid">
          {shown.map((it) => <LibTile key={it.id} it={it} selected={selMode && sel.includes(it.id)} onClick={() => (selMode ? toggleSel(it.id) : setOpen(it))} onDelete={selMode ? null : setDel} testid={`media-item-${it.id}`} />)}
        </div>
        {items && !shown.length && <p className="py-20 text-center text-sm text-zinc-500" data-testid="media-library-empty">No files match.</p>}
      </main>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-[#0E1114] border-zinc-800 text-zinc-100" data-testid="media-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{del?.bulk ? `Delete ${del.bulk.length} files permanently?` : "Delete permanently?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">{del?.bulk ? "Selected uploads and generated results are removed from history, the library and storage." : del?.source === "generated" ? "This generated result is removed from history, the library and storage." : "This upload is removed from the library and storage."} This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-700 text-zinc-300" data-testid="media-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 text-white hover:bg-red-400" data-testid="media-delete-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ImageViewer src={isImg ? mediaUrl(open.url) : null} onClose={() => setOpen(null)} />
      {open && <NavArrows {...libNav} portal testid="media-nav" />}
      {open && !isImg && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center gap-3 p-6" onClick={() => setOpen(null)} data-testid="media-preview">
          {(open.mime || "").startsWith("audio/") ? <audio src={mediaUrl(open.url)} controls autoPlay onClick={(e) => e.stopPropagation()} />
            : <video src={mediaUrl(open.preview_url || open.url)} controls autoPlay className="max-h-[80vh] max-w-full" onClick={(e) => e.stopPropagation()} />}
          <div className="text-sm text-zinc-300">{open.name}</div>
          <a href={mediaUrl(open.url)} download={open.name} onClick={(e) => { e.stopPropagation(); e.preventDefault(); saveAs(open.url, open.name || "media").catch(() => toast.error("Could not download")); }} className="ax-chip" data-testid="media-preview-download"><Download className="w-3.5 h-3.5" />Download original{/\.mov$/i.test(open.name || "") || open.mime === "video/quicktime" ? " (MOV)" : ""}</a>
        </div>
      )}
    </div>
  );
}
